import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, CalendarDays, CheckCircle2, Search, Trash2, UserPlus, Users } from 'lucide-react'
import { apiJson, type Page } from '../api'
import { complianceMeta, type Assignment } from './Compliance'

interface EmployeeRow {
  id: string
  employeeCode: string
  firstName: string
  lastName: string
  username?: string | null
}

interface CourseBrief {
  id: string
  title: string
  status: string
}

/** Admin/hr: assign a course to employees with a due date, and manage the assignment roster. */
export default function AssignmentForm() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [course, setCourse] = useState<CourseBrief | null>(null)
  const [roster, setRoster] = useState<Assignment[] | null>(null)
  const [employees, setEmployees] = useState<EmployeeRow[]>([])
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')

  // form state
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [dueDate, setDueDate] = useState('')
  const [mandatory, setMandatory] = useState(true)
  const [empQuery, setEmpQuery] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [actingId, setActingId] = useState<string | null>(null)

  const fetchAll = useCallback(async () => {
    try {
      const [c, r, e] = await Promise.all([
        apiJson<CourseBrief>(`/courses/${id}`),
        apiJson<Assignment[]>(`/courses/${id}/assignments`),
        apiJson<Page<EmployeeRow>>('/employees?size=200&sort=firstName&order=ASC'),
      ])
      setCourse(c)
      setRoster(r)
      setEmployees(e.data.filter((emp) => emp.username))
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignment data')
    }
  }, [id])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const assignedUsernames = useMemo(() => new Set((roster ?? []).map((a) => a.username)), [roster])

  const filtered = useMemo(() => {
    const q = empQuery.trim().toLowerCase()
    const pool = employees.filter((e) => !assignedUsernames.has(e.username!))
    if (!q) return pool
    return pool.filter(
      (e) =>
        `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
        e.username!.toLowerCase().includes(q) ||
        e.employeeCode.toLowerCase().includes(q),
    )
  }, [employees, empQuery, assignedUsernames])

  const toggle = (username: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(username)) next.delete(username)
      else next.add(username)
      return next
    })
  }

  const submit = async () => {
    if (selected.size === 0 || !dueDate) return
    setSubmitting(true)
    setError('')
    setSaved('')
    try {
      await apiJson(`/courses/${id}/assignments`, {
        method: 'POST',
        body: JSON.stringify({ usernames: [...selected], dueDate, mandatory }),
      })
      setSaved(`Assigned to ${selected.size} employee${selected.size === 1 ? '' : 's'} — they were enrolled automatically.`)
      setSelected(new Set())
      await fetchAll()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Assignment failed')
    } finally {
      setSubmitting(false)
    }
  }

  const patch = async (a: Assignment, body: Record<string, unknown>) => {
    setActingId(a.id)
    setError('')
    try {
      await apiJson(`/assignments/${a.id}`, { method: 'PATCH', body: JSON.stringify(body) })
      await fetchAll()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed')
    } finally {
      setActingId(null)
    }
  }

  const remove = async (a: Assignment) => {
    if (!window.confirm(`Remove the assignment for ${a.employeeName ?? a.username}?`)) return
    setActingId(a.id)
    setError('')
    try {
      await apiJson(`/assignments/${a.id}`, { method: 'DELETE' })
      await fetchAll()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setActingId(null)
    }
  }

  if (!course && !error) {
    return (
      <div className="flex h-48 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <div className="flex items-center justify-between">
        <Link to={`/courses/${id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
          <ArrowLeft size={15} /> Back to course
        </Link>
      </div>

      {error && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" /> {error}
        </div>
      )}
      {saved && (
        <div className="flex items-center gap-2.5 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <CheckCircle2 size={16} className="shrink-0" /> {saved}
        </div>
      )}

      <div>
        <h1 className="text-xl font-bold tracking-tight">Assign: {course?.title}</h1>
        <p className="mt-1 text-sm text-slate-500">
          Assigned employees are enrolled automatically and cannot drop a mandatory course.
        </p>
      </div>

      {/* ---- new assignment ---- */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="mb-1.5 block text-xs font-semibold text-slate-600">Employees ({selected.size} selected)</label>
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={empQuery}
                onChange={(e) => setEmpQuery(e.target.value)}
                placeholder="Search by name, username or code…"
                className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-slate-600">Due date</label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            />
          </div>
          <label className="flex items-center gap-2 pb-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={mandatory}
              onChange={(e) => setMandatory(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
            />
            Mandatory
          </label>
          <button
            onClick={submit}
            disabled={submitting || selected.size === 0 || !dueDate}
            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <UserPlus size={15} /> {submitting ? 'Assigning…' : 'Assign'}
          </button>
        </div>

        <div className="mt-3 max-h-56 overflow-y-auto rounded-xl border border-slate-100">
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-slate-400">
              {empQuery ? 'No matching employees.' : 'Every employee with a login is already assigned.'}
            </p>
          ) : (
            filtered.map((e) => (
              <label
                key={e.id}
                className="flex cursor-pointer items-center gap-3 border-b border-slate-50 px-4 py-2.5 transition-colors last:border-0 hover:bg-slate-50/70"
              >
                <input
                  type="checkbox"
                  checked={selected.has(e.username!)}
                  onChange={() => toggle(e.username!)}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm font-medium">{e.firstName} {e.lastName}</span>
                <span className="text-xs text-slate-400">{e.username} · {e.employeeCode}</span>
              </label>
            ))
          )}
        </div>
      </div>

      {/* ---- roster ---- */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4">
          <Users size={15} className="text-slate-400" />
          <h3 className="text-sm font-bold tracking-tight">Assignment roster ({roster?.length ?? 0})</h3>
        </div>
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/60">
              <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Employee</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Due</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Status</th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Progress</th>
              <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(roster ?? []).map((a) => {
              const st = complianceMeta[a.compliance]
              return (
                <tr key={a.id} className="transition-colors hover:bg-slate-50/70">
                  <td className="px-5 py-3.5">
                    <p className="text-sm font-semibold">{a.employeeName ?? a.username}</p>
                    <p className="text-xs text-slate-400">
                      {a.username}
                      {a.recertification && ' · recertification'}
                      {!a.mandatory && ' · optional'}
                    </p>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${a.compliance === 'overdue' ? 'text-red-600' : 'text-slate-600'}`}>
                      <CalendarDays size={12} /> {a.dueDate}
                    </span>
                    {a.compliance !== 'completed' && a.compliance !== 'exempted' && (
                      <p className="text-[11px] text-slate-400">
                        {a.daysUntilDue < 0 ? `${-a.daysUntilDue}d overdue` : a.daysUntilDue === 0 ? 'due today' : `${a.daysUntilDue}d left`}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-slate-500">
                    {a.enrollmentProgress !== null ? `${a.enrollmentProgress}%` : '—'}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      {a.status === 'assigned' && (
                        <button
                          onClick={() => patch(a, { status: 'exempted' })}
                          disabled={actingId === a.id}
                          className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-40"
                        >
                          Exempt
                        </button>
                      )}
                      {a.status === 'exempted' && (
                        <button
                          onClick={() => patch(a, { status: 'assigned' })}
                          disabled={actingId === a.id}
                          className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-brand-600 transition-colors hover:bg-brand-50 disabled:opacity-40"
                        >
                          Reinstate
                        </button>
                      )}
                      <button
                        onClick={() => remove(a)}
                        disabled={actingId === a.id}
                        className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                        title="Remove assignment"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {(roster ?? []).length === 0 && (
          <div className="flex flex-col items-center py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <UserPlus size={22} />
            </div>
            <p className="mt-3 text-sm font-semibold">No assignments yet</p>
            <p className="mt-1 text-xs text-slate-400">Pick employees above to assign this course.</p>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => navigate('/learning')}
          className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
        >
          Done
        </button>
      </div>
    </div>
  )
}
